import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Text } from 'components/Text';
import { Stats } from 'components/Stats';
import AdvancedFilter, {
  AppliedFilter as AdvAppliedFilter,
  FilterDef as AdvFilterDef,
} from 'components/AdvancedFilter';
import { SearchBox } from 'components/Search';
import { Pagination } from 'components/Pagination';
import { DatePicker } from 'components/DatePicker';
import { Button } from 'components/Button';
import { columns } from './data/claims-data';
import { LoadingScreen } from 'components/LoadingScreen';
import {
  GET_CLAIMS_BY,
  GET_CLAIMS_COUNT,
  GET_ORDERS_CLAIMS_COUNT,
  FILTER_CLAIMS,
} from 'gql/queries';
import { withNotification } from 'components/Notification';
import { apiErrorHandler, NotificationTypes } from 'utils';
import { Claim, ResolutionType } from 'types/claim';
import { useStore } from 'context/store-context';
import { CustomerPortalBtn } from 'components/CustomerPortalBtn';
import { ClaimsTable } from './ClaimsTable';
import { DESTROY_CLAIM } from 'gql/mutations';
import withConfirmation from 'hooks/withConfirmation';
import { presetToLocalRange, customToLocalRange } from 'utils/date';

export const Claims = withConfirmation(
  withNotification(({ showNotification, setShowConfirmation }: any) => {
    const limit = 20;
    const [allClaims, setAllClaims] = useState<[]>([]);
    const [, setOrdersCount] = useState(0);
    const [claimsCount, setClaimsCount] = useState(0);
    const [filterInput, setFilterInput] = useState<any>({});
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(1);
    const [offset, setOffset] = useState(0);
    const [dateRangeStats, setDateRangeStats] = useState<
      [Date | null, Date | null]
    >([new Date(), new Date()]);
    const [advancedFilters, setAdvancedFilters] = useState<AdvAppliedFilter[]>(
      [],
    );

    const navigate = useNavigate();
    const { storeId, storeProperties } = useStore();

    const [getClaimsCount, { data: countData }] =
      useLazyQuery(GET_CLAIMS_COUNT);

    useEffect(() => {
      storeId &&
        getClaimsCount({
          variables: {
            storeId,
          },
        });
    }, [storeId, getClaimsCount]);

    const [getTotalOrderandClaims] = useLazyQuery(GET_ORDERS_CLAIMS_COUNT, {
      onCompleted: (data) => {
        setOrdersCount(data.totalOrders?.aggregate.total);
      },
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    });

    const [destroyClaim] = useMutation(DESTROY_CLAIM, {
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
      onCompleted(data) {
        showNotification(NotificationTypes.success, 'Issue deleted');

        return setTimeout(() => {
          return window.location.reload();
        }, 300);
      },
    });

    const [filterClaims, { data, loading }] = useLazyQuery(FILTER_CLAIMS, {
      onCompleted: (data) => {
        setAllClaims(data?.claims);
        setClaimsCount(data?.claimsAggregate?.aggregate?.count);
      },
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    });

    const [orderedClaims] = useLazyQuery(GET_CLAIMS_BY, {
      onCompleted: (data) => {
        setAllClaims(data?.claims);
      },
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(NotificationTypes.error, newError?.message);
      },
    });

    useEffect(() => {
      if (storeId) {
        getTotalOrderandClaims({
          variables: {
            storeId,
          },
        });
      }
    }, [getTotalOrderandClaims, offset, storeId]);

    useEffect(() => {
      filterClaims({
        variables: {
          filters: { ...filterInput, storeId: { _eq: storeId } },
          limit,
          offset,
          order_by: [
            {
              createdAt: 'desc',
            },
          ],
        },
      });
    }, [filterInput, filterClaims, storeId, offset]);

    useEffect(() => {
      setPage(1);
      setOffset(0);

      // Build a fresh base to avoid stale clauses when removing filters
      const next: any = {};
      if (searchValue && searchValue.trim().length) {
        next._or = [
          { number: { _ilike: `%${searchValue}%` } },
          { order: { number: { _ilike: `%${searchValue}%` } } },
          { order: { customerEmail: { _ilike: `%${searchValue}%` } } },
          { order: { customerName: { _ilike: `%${searchValue}%` } } },
        ];
      }

      advancedFilters.forEach((f) => {
        if (f.key === 'dateRange') {
          const vals = f.values;
          // token from legacy 'token:xxx'
          if (vals.length === 1 && vals[0].startsWith('token:')) {
            const token = vals[0].replace('token:', '') as any;
            const r = presetToLocalRange(token);
            if (r) {
              const exist = Array.isArray(next._and) ? next._and : [];
              next._and = [
                ...exist,
                { createdAt: { _gte: r.startISO } },
                { createdAt: { _lt: r.endISO } },
              ];
            }
          } else if (vals.length >= 2 && vals[0] === 'preset') {
            const token = vals[1] as any;
            const r = presetToLocalRange(token);
            if (r) {
              const exist = Array.isArray(next._and) ? next._and : [];
              next._and = [
                ...exist,
                { createdAt: { _gte: r.startISO } },
                { createdAt: { _lt: r.endISO } },
              ];
            }
          } else if (vals.length >= 3 && vals[0] === 'custom') {
            const from = vals[1];
            const to = vals[2];
            if (from && to) {
              const r = customToLocalRange(from, to);
              const exist = Array.isArray(next._and) ? next._and : [];
              next._and = [
                ...exist,
                { createdAt: { _gte: r.startISO } },
                { createdAt: { _lt: r.endISO } },
              ];
            }
          }
        }
        if (f.key === 'reason') {
          const mapped = f.values.map((v) => v.toUpperCase());
          if (mapped.length === 1) next.reason = { _eq: mapped[0] };
          else if (mapped.length > 1) next.reason = { _in: mapped };
        }
        if (f.key === 'status') {
          const mapped = f.values.map((v) =>
            v.split(' ').join('_').toUpperCase(),
          );
          if (mapped.length === 1) next.status = { _eq: mapped[0] };
          else if (mapped.length > 1) next.status = { _in: mapped };
        }
        if (f.key === 'resolution') {
          const mapped = f.values.map((v) => {
            if (v === 'Deny') return ResolutionType.deny;
            if (v === 'Reorder') return ResolutionType.reorder;
            return v.split(' ').join('_').toUpperCase();
          });
          if (mapped.length === 1) next.resolution = { _eq: mapped[0] };
          else if (mapped.length > 1) next.resolution = { _in: mapped };
        }
      });

      setFilterInput(next);
    }, [advancedFilters, searchValue]);

    const search = () => {
      setFilterInput({
        ...filterInput,
        _or: [
          { number: { _ilike: `%${searchValue}%` } },
          { order: { number: { _ilike: `%${searchValue}%` } } },
          { order: { customerEmail: { _ilike: `%${searchValue}%` } } },
          { order: { customerName: { _ilike: `%${searchValue}%` } } },
        ],
      });
    };

    const handleSearch = (value: string) => {
      if (value === '') {
        const obj = { ...filterInput };
        delete obj._or;
        setSearchValue(value);
        /**
       * this was done this way to ensure that whenever the search is cleared, 
        it also checks that there are no filter rules there to ensure that it sets the 
        table back to default
      */
        Object.keys(obj).length === 0 && setAllClaims(data?.claims);
        return setFilterInput(obj);
      }
      setSearchValue(value);
    };

    const onPageChange = (pageNumber: number) => {
      setPage(pageNumber);
      if (pageNumber < page) {
        if (pageNumber - 1 === 0) {
          return setOffset(0);
        }
        return setOffset(limit * (pageNumber - 1));
      }
      return setOffset(limit * (pageNumber - 1));
    };

    const onViewClaim = (claim: Claim) => {
      navigate(`/claims/${claim.id}`);
    };

    const handleNewClaim = () => {
      navigate('/claims/new-claim', { state: { returnLocation: '/claims' } });
    };

    const handleOrderByColumn = (column: string, direction: string) => {
      orderedClaims({
        variables: {
          order_by: [
            {
              [column]: direction,
            },
          ],
        },
      });
    };

    const onDeleteClaim = useCallback(
      (claim: any) => {
        setShowConfirmation({
          title: 'Delete issue',
          message: 'Are you sure you want to delete this issue?',
          subMessage: 'This issue will be permanently deleted.',
          type: NotificationTypes.error,
          onConfirm: () => {
            if (storeId) {
              destroyClaim({
                variables: {
                  input: {
                    storeId,
                    claimId: claim.id,
                  },
                },
              });
            }
          },
          btnText: 'Confirm',
        });
      },
      [storeId, setShowConfirmation, destroyClaim],
    );

    const stats = [
      {
        name: 'New Issues',
        currentStat: countData ? `${countData.newClaims.aggregate.count}` : 0,
      },
      {
        name: 'Resolved Issues',
        currentStat: `${
          (countData && countData?.resolvedClaims.aggregate.count) ?? 0
        }`,
      },
      {
        name: 'Total Issues',
        currentStat: `${
          (countData && countData?.totalClaims.aggregate.count) ?? 0
        }`,
      },
    ];

    const reasonFilterOptions = ['Lost', 'Stolen', 'Damaged', 'Late', 'Other'];

    const statusFilterOptions = ['New', 'Resolved', 'In Review', 'Denied'];

    const resolutionFilterOptions = ['In Review', 'Deny', 'Reorder', 'Refund'];

    const advancedFilterDefs: AdvFilterDef[] = [
      {
        key: 'reason',
        label: 'Reason',
        type: 'multiselect',
        options: reasonFilterOptions.map((o) => ({ label: o, value: o })),
      },
      {
        key: 'status',
        label: 'Status',
        type: 'multiselect',
        options: statusFilterOptions.map((o) => ({ label: o, value: o })),
      },
      {
        key: 'resolution',
        label: 'Resolution',
        type: 'multiselect',
        options: resolutionFilterOptions.map((o) => ({ label: o, value: o })),
      },
      {
        key: 'dateRange',
        label: 'Date',
        type: 'dateRange',
        options: [],
      },
    ];

    if (loading) return <LoadingScreen />;

    const orderByCols = ['createdAt', 'total'];
    const claims_count = claimsCount;
    // Object.keys(filterInput).length === 0 ? claimsCount : allClaims.length;
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="md:flex justify-between items-center">
          <div className="md:flex items-center">
            <Text value="All Issues" type="h4" />
            <DatePicker
              selectsRange
              startDate={dateRangeStats[0]}
              endDate={dateRangeStats[1]}
              setValue={setDateRangeStats}
              placeHolder="Date Range"
              showYearDropdown
              isClearable
              className="md:ml-5 my-3 sm:my-0 w-80 sm:w-fit"
            />
          </div>
          <div>
            <CustomerPortalBtn />
            <Button onClick={handleNewClaim} className="sm:mt-2 ml-2">
              New Delivery Issue
            </Button>
          </div>
        </div>
        <div className="">
          <Stats stats={stats} isSpaced />
        </div>
        <div className="md:flex justify-between mb-2 mt-12 sm:block">
          <div className="mb-4 sm:mb-4 md:mb-2 flex items-center">
            <div className="w-[25rem]">
              <SearchBox
                search={search}
                searchValue={searchValue}
                onChange={(searchValue) => handleSearch(searchValue)}
                placeholder="Search by Issue no, Order no, Name, Email"
              />
            </div>
            {loading && (
              <div
                style={{
                  borderColor: 'rgba(37,99,235, 0.2)',
                  borderTopColor: '#2564eb',
                }}
                className="inline-block border-2 rounded-full animate-spin w-3 h-3 ml-2"
              />
            )}
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-2">
          <AdvancedFilter
            className="mr-4"
            filters={advancedFilterDefs}
            applied={advancedFilters}
            onChange={setAdvancedFilters}
          />
        </div>
        <div className="">
          <ClaimsTable
            claims={
              allClaims?.length > 0
                ? [...allClaims].map((claim: Claim) => ({
                    ...claim,
                    onViewClaim: () => onViewClaim(claim),
                    onDeleteClaim: () => onDeleteClaim(claim),
                    currency: storeProperties?.currency,
                  }))
                : []
            }
            columns={columns}
            emptyMessage={
              searchValue
                ? `No search results matches "${searchValue}"`
                : 'No data'
            }
            orderByOptions={orderByCols}
            handleSelectedColumn={handleOrderByColumn}
          />
        </div>
        <div className="mt-3">
          {claims_count > limit && (
            <Pagination
              totalPages={Math.ceil(claims_count / limit)}
              currentPage={page}
              resultsRange={limit}
              totalResults={claims_count ?? 0}
              isFilterApplied={Object.keys(filterInput).length ? true : false}
              onPageClick={onPageChange}
            />
          )}
        </div>
      </div>
    );
  }),
);
